import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Loader, ExternalLink, Mail, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  docketFollowService,
  type FollowedDocketWithMeta,
  type DigestFrequency,
} from '../lib/docketFollowService';
import {
  getDigestHistory,
  type DigestHistoryEntry,
} from '../lib/watchlistDigestService';

const FREQ_OPTIONS: { value: DigestFrequency; label: string }[] = [
  { value: 'immediate', label: 'Immediate' },
  { value: 'daily', label: 'Daily digest' },
  { value: 'weekly', label: 'Weekly digest' },
  { value: 'none', label: 'Off' },
];

export const WatchlistNotificationSettings: React.FC = () => {
  const navigate = useNavigate();
  const [follows, setFollows] = useState<FollowedDocketWithMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);

  useEffect(() => {
    docketFollowService
      .getFollowedDockets()
      .then(setFollows)
      .catch(err => console.error('Error loading follows:', err))
      .finally(() => setLoading(false));
  }, []);

  const handleFrequencyChange = async (
    docketNumber: string,
    freq: DigestFrequency
  ) => {
    try {
      setUpdating(docketNumber);
      await docketFollowService.updateDigestFrequency(docketNumber, freq);
      setFollows(prev =>
        prev.map(f =>
          f.docket_number === docketNumber
            ? { ...f, digest_frequency: freq, notify_new_filings: freq !== 'none' }
            : f
        )
      );
    } catch (err) {
      console.error('Error updating frequency:', err);
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
        <Loader className="h-4 w-4 animate-spin" />
        Loading watchlist settings...
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-black">Watchlist Alerts</h3>
        <button
          onClick={() => navigate('/watchlist')}
          className="flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-700 transition-colors"
        >
          Manage watchlist
          <ExternalLink className="h-3 w-3" />
        </button>
      </div>

      {follows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500">
          <BellOff className="mx-auto mb-2 h-5 w-5 text-gray-300" />
          <p>No proceedings on your watchlist yet.</p>
          <button
            onClick={() => navigate('/watchlist')}
            className="mt-2 text-emerald-600 hover:text-emerald-700 font-medium"
          >
            Add proceedings to watch
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-gray-500 mb-3">
            Choose how often you want to be notified about new filings for each followed proceeding.
          </p>
          {follows.map((follow) => (
            <div
              key={follow.id}
              className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3"
            >
              <div className="min-w-0 flex-1 mr-3">
                <div className="flex items-center gap-2">
                  {follow.notify_new_filings ? (
                    <Bell className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
                  ) : (
                    <BellOff className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                  )}
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {follow.docket_number}
                  </span>
                </div>
                {follow.proceeding_name && (
                  <p className="mt-0.5 text-xs text-gray-500 truncate ml-6">
                    {follow.proceeding_name}
                  </p>
                )}
              </div>
              <div className="shrink-0">
                {updating === follow.docket_number ? (
                  <Loader className="h-4 w-4 animate-spin text-gray-400" />
                ) : (
                  <select
                    value={follow.digest_frequency}
                    onChange={(e) =>
                      handleFrequencyChange(
                        follow.docket_number,
                        e.target.value as DigestFrequency
                      )
                    }
                    className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  >
                    {FREQ_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Email History */}
      <DigestEmailHistory />
    </div>
  );
};

// ── Digest Email History ─────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  sent: { icon: <Clock className="h-3 w-3" />, color: 'text-amber-600', label: 'Sent' },
  delivered: { icon: <CheckCircle className="h-3 w-3" />, color: 'text-emerald-600', label: 'Delivered' },
  failed: { icon: <XCircle className="h-3 w-3" />, color: 'text-red-600', label: 'Failed' },
  bounced: { icon: <XCircle className="h-3 w-3" />, color: 'text-red-600', label: 'Bounced' },
};

const FREQ_LABELS: Record<string, string> = {
  immediate: 'Immediate',
  daily: 'Daily',
  weekly: 'Weekly',
};

const DigestEmailHistory: React.FC = () => {
  const [emails, setEmails] = useState<DigestHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    getDigestHistory(10)
      .then((data) => setEmails(data.emails))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="mt-6 flex items-center gap-2 text-sm text-gray-400">
        <Loader className="h-3.5 w-3.5 animate-spin" />
        Loading email history...
      </div>
    );
  }

  if (error) return null;

  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 mb-3">
        <Mail className="h-4 w-4 text-gray-400" />
        <h3 className="text-sm font-semibold text-gray-700">Email History</h3>
      </div>

      {emails.length === 0 ? (
        <p className="text-sm text-gray-500">
          No digest emails sent yet. Once your watched dockets have new filings, you'll start
          receiving digest emails based on your frequency settings.
        </p>
      ) : (
        <div className="space-y-2">
          {emails.map((email) => {
            const statusCfg = STATUS_CONFIG[email.status] || STATUS_CONFIG.sent;
            const sentDate = new Date(email.sent_at);

            return (
              <div
                key={email.id}
                className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3"
              >
                <div className="min-w-0 flex-1 mr-3">
                  <div className="flex items-center gap-2 text-sm">
                    <span className={`flex items-center gap-1 ${statusCfg.color}`}>
                      {statusCfg.icon}
                      {statusCfg.label}
                    </span>
                    <span className="text-gray-300">·</span>
                    <span className="text-gray-600 font-medium">
                      {FREQ_LABELS[email.frequency] || email.frequency} digest
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {email.dockets_included.length} docket{email.dockets_included.length !== 1 ? 's' : ''}
                    {' · '}
                    {email.total_filings_reported} filing{email.total_filings_reported !== 1 ? 's' : ''} reported
                  </p>
                  <p className="mt-0.5 text-[11px] text-gray-400 truncate">
                    {email.dockets_included.join(', ')}
                  </p>
                </div>
                <span className="shrink-0 text-xs text-gray-400">
                  {sentDate.toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric',
                  })}
                  {' '}
                  {sentDate.toLocaleTimeString('en-US', {
                    hour: 'numeric', minute: '2-digit',
                  })}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
